<template>
  <div class="language-container">
    <div
      class="dropdown-trigger"
      :class="{
        active: isVisibleDropdownLanguage,
      }"
      @click="onClickButtonLanguage"
    >
      <p>
        {{ selectedLanguage }}
      </p>
      <component
        class="icon-chevron icon-medium icon-default icon-dropdown"
        :is="isVisibleDropdownLanguage ? ChevronUpIcon : ChevronDownIcon"
      />
    </div>
    <div v-if="isVisibleDropdownLanguage" class="language-options">
      <LanguageList
        :showAllInList="true"
        :selected-at-top="true"
        is-using-language-detection
        @select="changeLanguage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { cloneDeep } from 'lodash';

import ChevronDownIcon from '@/components/icons/IconChevronDown.vue';
import ChevronUpIcon from '@/components/icons/IconChevronUp.vue';
import LanguageList from '@/components/shared/base/LanguageList.vue';
import { useAudioStore } from '@/stores/audio';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { Language } from '@/types/common';

const props = withDefaults(
  defineProps<{
    isGlobal?: boolean;
    languageDefault?: Language | null;
  }>(),
  {
    isGlobal: true,
    languageDefault: null,
  }
);

const { t } = useI18n();
const socketStore = useSocketStore();
const audioStore = useAudioStore();
const toastStore = useToastStore();

const modelValue = defineModel<Language | null>();

const isVisibleDropdownLanguage = ref(false);

const selectedLanguage = computed(() => {
  const langCode = props.isGlobal ? audioStore.languageSelected?.code : modelValue.value?.code;
  const localeName = ` (${t(`label_language_${langCode}`)})`;

  if (props.isGlobal) {
    const langName = audioStore.languageSelected?.name;

    return langCode && langName ? langName + localeName : t('choose_language');
  }

  return modelValue.value?.name + localeName || t('choose_language');
});

const openDropdownLanguage = () => {
  isVisibleDropdownLanguage.value = true;
};

const closeDropdownLanguage = () => {
  isVisibleDropdownLanguage.value = false;
};

const onClickButtonLanguage = () => {
  if (isVisibleDropdownLanguage.value) {
    closeDropdownLanguage();
  } else {
    openDropdownLanguage();
  }
};

const changeLanguage = (language) => {
  try {
    if (!props.isGlobal) {
      modelValue.value = language;
      closeDropdownLanguage();
      return;
    }

    socketStore.sendMessageChangeLanguage(language.code);
    audioStore.selectLanguage(language.code);

    modelValue.value = language;
    closeDropdownLanguage();

    toastStore.info(language.name, 'LANGUAGE');
  } catch (e) {
    toastStore.error(t('toast_failed_to_change_language'), 'LANGUAGE');
  }
};

watch(
  () => props.languageDefault,
  (newVal) => {
    if (!newVal || newVal?.code === modelValue.value?.code) return;

    modelValue.value = cloneDeep(newVal);
  },
  { immediate: true, deep: true }
);
</script>

<style scoped lang="scss">
.dropdown-trigger {
  display: flex;
  width: 100%;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.75rem;
  background-color: var(--surface-gray-dim);
  border-radius: 8px;
  cursor: pointer;
}

.dropdown-trigger:hover,
.dropdown-trigger:active,
.dropdown-trigger.active {
  background-color: var(--surface-gray-dim);
}

::v-deep(.dropdown-content) {
  width: 77dvw;
  max-width: 21rem;
}

.language-container {
  width: 100%;
  position: relative;
}

.language-options {
  position: absolute;
  width: 100%;
  z-index: 999;
  max-height: 300px;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: var(--surface-default);
  border-radius: 8px;
  overflow-y: auto;
}

.icon-chevron {
  position: absolute;
  right: 1rem;
}
</style>
